import React from 'react';
import { Link } from 'react-router-dom';

const DriftPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Drift - Sleep Tracker by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Last updated: 17 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Drift - Sleep Tracker ("Drift", "we", "our", "us") is a sleep tracking application
              developed and operated by Wolf Technologies Ltd. This privacy policy explains how we
              collect, use, store, and protect your personal data when you use the Drift app on iOS
              or the web.
            </p>
            <p>
              We are committed to protecting your privacy and complying with the UK General Data
              Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
            <p>
              <strong>Data Controller:</strong> Wolf Technologies Ltd<br />
              <strong>Contact:</strong>{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>When you use Drift, we collect the following data:</p>
            <ul>
              <li><strong>Account information</strong> — email address, used for authentication and account management.</li>
              <li><strong>Sleep logs</strong> — bedtime, wake time, sleep quality rating, and sleep factors you choose to record (e.g. caffeine, exercise, alcohol, stress, screen time).</li>
              <li><strong>Sleep scores</strong> — calculated scores based on your logged sleep data, including duration, timing, consistency, and quality metrics.</li>
              <li><strong>Sleep debt</strong> — accumulated sleep deficit tracking based on your logged entries.</li>
              <li><strong>Subscription status</strong> — whether you have an active premium subscription, the plan type (monthly, annual, or lifetime), and expiry date.</li>
            </ul>
            <p>
              All sleep data is entered manually by you. We do not collect data automatically from
              wearables, sensors, or other devices.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>Drift does <strong>not</strong> collect:</p>
            <ul>
              <li><strong>Biometric data</strong> (heart rate, blood oxygen, HRV, or any sensor-based health metrics)</li>
              <li><strong>GPS or location data</strong></li>
              <li><strong>Contacts</strong> or address book information</li>
              <li><strong>Health sensor data</strong> (we do not integrate with HealthKit, Google Fit, or any wearable device)</li>
              <li><strong>Photos, camera, or microphone data</strong></li>
              <li><strong>Browsing history</strong> or search history</li>
            </ul>
            <p>Drift is a manual sleep logging app. All data is entered by you directly.</p>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use the data you provide to:</p>
            <ul>
              <li><strong>Calculate sleep scores</strong> — analyse your logged bedtime, wake time, quality, and factors to generate a composite sleep score.</li>
              <li><strong>Track sleep debt</strong> — monitor your accumulated sleep deficit over time using a battery metaphor for easy understanding.</li>
              <li><strong>Generate insights</strong> — identify patterns and correlations between your sleep factors and sleep quality.</li>
              <li><strong>Provide trends and history</strong> — show your sleep patterns over time (30-day trends, streaks, and consistency metrics).</li>
              <li><strong>Manage subscriptions</strong> — process and validate your premium subscription status.</li>
              <li><strong>Improve the app</strong> — aggregate, anonymised usage patterns may be used to improve the app experience (no individual data is shared).</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>Your data is stored securely using Amazon Web Services (AWS) infrastructure:</p>
            <ul>
              <li><strong>Region</strong> — EU West 2 (London, UK).</li>
              <li><strong>Encryption at rest</strong> — all data stored in DynamoDB is encrypted using AWS-managed encryption keys.</li>
              <li><strong>Encryption in transit</strong> — all data transmitted between your device and our servers uses TLS 1.2+ encryption.</li>
              <li><strong>Authentication</strong> — user accounts are managed through AWS Cognito with secure password hashing and token-based authentication.</li>
            </ul>
            <p>We follow industry best practices for data security and regularly review our security measures.</p>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>Drift uses the following third-party services to operate:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — user authentication and account management. Receives email address and encrypted password.</li>
              <li><strong>AWS DynamoDB</strong> — data storage for sleep logs, scores, and preferences. Stores all user-entered sleep data.</li>
              <li><strong>Stripe</strong> — web payment processing for premium subscriptions. Receives email address and payment details (handled directly by Stripe).</li>
              <li><strong>Apple In-App Purchase</strong> — iOS payment processing for premium subscriptions. Receives Apple ID and transaction details (handled directly by Apple).</li>
            </ul>
            <p>
              Payment details (card numbers, billing addresses) are processed directly by Stripe and
              Apple. We do not store or have access to your full payment card details.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              We do <strong>not</strong> sell, rent, or share your personal data with third parties for
              marketing or advertising purposes.
            </p>
            <p>Your data is only shared with:</p>
            <ul>
              <li><strong>Payment processors</strong> (Stripe and Apple) solely for the purpose of processing subscription payments.</li>
              <li><strong>AWS infrastructure services</strong> solely for the purpose of storing and serving your data back to you.</li>
            </ul>
            <p>
              We will disclose your data if required to do so by law or in response to valid legal
              requests by public authorities.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <ul>
              <li><strong>Active accounts</strong> — your data is retained for as long as your account is active.</li>
              <li><strong>Account deletion</strong> — you can delete your account at any time from Settings {'>'} Delete Account within the app.</li>
              <li><strong>Deletion timeline</strong> — upon account deletion, all your personal data (sleep logs, scores, preferences, and account information) will be permanently removed from our servers within 30 days.</li>
              <li><strong>Subscription data</strong> — if you have an active subscription at the time of deletion, it will not be automatically cancelled. Please cancel your subscription before deleting your account.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights (GDPR)</h2>
            <p>
              Under the UK GDPR, you have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification</strong> — request correction of inaccurate personal data.</li>
              <li><strong>Erasure</strong> — request deletion of your personal data.</li>
              <li><strong>Data portability</strong> — request your data in a structured, commonly used, machine-readable format.</li>
              <li><strong>Restriction</strong> — request that we limit how we use your data.</li>
              <li><strong>Objection</strong> — object to processing of your personal data.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>. We will
              respond to your request within 30 days.
            </p>
            <p>
              You also have the right to lodge a complaint with the Information Commissioner's Office
              (ICO) at https://ico.org.uk.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. When we make changes, we will
              update the "Last updated" date at the top of this page. For significant changes, we
              will notify you via email or through an in-app notification.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact</h2>
            <p>
              If you have any questions about this privacy policy or how we handle your data, please
              contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default DriftPrivacy;
